/**
 * ProductActionDrawer - Side-Panel für Readiness-Aktionen
 * Plan 012: Merge Products + Channel Readiness
 *
 * Öffnet sich beim Klick auf ReadinessActionBadge.
 * Zeigt: DrawerHeader + DrawerAssetPool + DrawerChannelRules + Footer.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sheet, SheetContent, SheetFooter, SheetClose, Button, Checkbox } from '@/components/v2/ui'
import { getProductAssets } from '@/lib/api/products'
import { useProductReadiness, useReadinessSummary } from '@/hooks/useChannelReadiness'
import { DrawerHeader } from './DrawerHeader'
import { DrawerAssetPool } from './DrawerAssetPool'
import { DrawerChannelRules } from './DrawerChannelRules'
import type { ReadinessActionState } from '@/types/products'

interface ProductActionDrawerProps {
  open: boolean
  productId: string | null
  productName?: string
  productSku?: string | null
  thumbnailUrl?: string | null
  state?: ReadinessActionState
  onClose: () => void
}

export function ProductActionDrawer({
  open, productId, productName, productSku, thumbnailUrl, state, onClose,
}: ProductActionDrawerProps) {
  // TODO Phase 5: wire reviewed state to mutation (mark product as reviewed)
  const [reviewed, setReviewed] = useState(false)

  const scoresQuery = useProductReadiness(open ? productId : null)
  const summaryQuery = useReadinessSummary()
  const assetsQuery = useQuery({
    queryKey: ['productAssets', productId],
    queryFn: () => getProductAssets(productId!),
    enabled: open && !!productId,
    staleTime: 30_000,
  })

  const scores = scoresQuery.data ?? []
  const assets = assetsQuery.data?.assets ?? []
  const channelNames: Record<string, string> = Object.fromEntries(
    (summaryQuery.data ?? []).map(s => [s.channel_id, s.channel_name])
  )

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="product-action-drawer w-[45vw] min-w-[400px] max-w-none p-0 flex flex-col dark:bg-neutral-900 dark:border-neutral-800"
        aria-labelledby="product-action-drawer-title"
      >
        <DrawerHeader
          productName={productName ?? ''}
          sku={productSku ?? null}
          thumbnailUrl={thumbnailUrl}
          state={state}
          onClose={onClose}
        />
        <div className="flex-1 overflow-y-auto divide-y divide-neutral-200 dark:divide-neutral-700">
          <DrawerAssetPool assets={assets} channelScores={scores} channelNames={channelNames} />
          <DrawerChannelRules scores={scores} channelNames={channelNames} />
        </div>
        <SheetFooter className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3 flex-row items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
            <Checkbox
              checked={reviewed}
              onChange={e => setReviewed(e.target.checked)}
            />
            Auf &apos;Geprüft&apos; setzen
          </label>
          <SheetClose asChild>
            <Button variant="outline" size="sm" onClick={onClose}>Schließen</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
